/** PVM AG / Global Premium Select (P share) — SHV V4 layout with GPS page-2 allocations. */
export const TEMPLATE_ID_PVM_GPS = '69d0c43f5a2b1c0d9e8f7a6b';

export const GPS_HISTORY_ISIN = 'LU1551351312';

/** Official fund name shown in header. */
export const PVM_GPS_FOND_TITLE = 'Global Premium Select';

/** Default SHV / Frankfurter ETF header title (space before hyphen). */
export const SHV_DEFAULT_FOND_TITLE = 'Frankfurter UCITS-ETF - Modern Value';

/** P share class suffix in header (` P`, no pipe — matches chart legend). */
export const PVM_GPS_DEFAULT_TITLE_ADDITION = 'P';

/** Line-chart legend + composed headline (`Global Premium Select P`). */
export const PVM_GPS_HISTORY_LEGEND_LABEL = 'Global Premium Select P';

export function isPvmGpsTemplate(templateId?: string): boolean {
  return templateId === TEMPLATE_ID_PVM_GPS;
}

/** Normalize NFI/DB names to the official GPS header title. */
export function normalizePvmGpsFondTitle(fond?: string | null): string {
  const trimmed = fond?.trim();
  if (!trimmed || /frankfurter/i.test(trimmed)) return PVM_GPS_FOND_TITLE;
  if (/global\s+premium/i.test(trimmed)) return PVM_GPS_FOND_TITLE;
  return trimmed;
}

/**
 * Ensure SHV ETF title uses spaced hyphen: "UCITS-ETF - Modern Value"
 * (DB/NFI often store "UCITS-ETF-Modern Value" without the space).
 */
export function normalizeShvFondTitle(fond?: string | null): string {
  const trimmed = fond?.trim();
  if (!trimmed) return SHV_DEFAULT_FOND_TITLE;
  return trimmed.replace(/UCITS-ETF\s*-\s*Modern/gi, 'UCITS-ETF - Modern');
}

/** Use GPS fund title; ignore Frankfurter `fond` left over from SHV clone. */
export function resolvePvmGpsFondTitle(templateId?: string, templateFond?: string | null): string {
  if (!isPvmGpsTemplate(templateId)) {
    return normalizeShvFondTitle(templateFond);
  }
  return normalizePvmGpsFondTitle(templateFond);
}

/**
 * Upgrade stale legend strings (`Global Premium P`, Frankfurter leftovers)
 * to the official GPS label.
 */
export function resolvePvmGpsHistoryLegendLabel(raw?: string | null): string {
  const t = (raw || '').trim();
  if (!t || /frankfurter/i.test(t)) return PVM_GPS_HISTORY_LEGEND_LABEL;
  if (/^global\s+premium(\s+select)?(\s+p)?$/i.test(t)) {
    return PVM_GPS_HISTORY_LEGEND_LABEL;
  }
  if (/global\s+premium/i.test(t) && !/select/i.test(t)) {
    return t.replace(/global\s+premium/i, 'Global Premium Select');
  }
  return t;
}

/** True when a bar-chart footer label is an SHV leftover that must not show on PVM. */
export function isPvmShvBarChartLabel(raw?: string | null): boolean {
  const t = (raw || '').trim();
  if (!t) return true;
  return /frankfurter/i.test(t);
}

export const GPS_SECTION_HEADER_DEFAULTS = {
  assetAllocation: 'VERMÖGENSAUFTEILUNG',
  sectorAllocation: 'BRANCHEN (AKTIENANTEIL)',
  top10Stocks: 'TOP-10 AKTIEN (in %)',
} as const;

/** Page 2 left column — image slot (Assenagon-style layout). */
export const PVM_PAGE2_LEFT_IMAGE_LABEL =
  'Bildplatzhalter — Grafik / Fondsmanager-Foto (wird manuell hinterlegt)';

/** Page 2 left column — second image slot under the first. */
export const PVM_PAGE2_LEFT_IMAGE_LABEL_2 =
  'Bildplatzhalter — Zusatzgrafik (wird manuell hinterlegt)';

/** Page 2 left column — third image slot under the second. */
export const PVM_PAGE2_LEFT_IMAGE_LABEL_3 =
  'Bildplatzhalter — Weitere Grafik (wird manuell hinterlegt)';

/** Shared outer box width (pt) — full-width award row (~20% under prior 132pt). */
export const PVM_PAGE2_LEFT_IMAGE_WIDTH = 106;
/** Placeholder empty-slot height only — real images use width + natural aspect ratio. */
export const PVM_PAGE2_LEFT_IMAGE_MAX_HEIGHT = 70;
/** Shared gap between page-2 award images. */
export const PVM_PAGE2_LEFT_IMAGE_GAP = 8;
